import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { HeartHandshake, ShieldCheck } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_optia-telemedicine/artifacts/d09ijb3r_IMG_6632.jpeg";

export default function SusPortal() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { login, user } = useAuth();
  const [muni, setMuni] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/municipalities/slug/${slug}`).then((r) => setMuni(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (user?.municipality_slug === slug) nav("/dashboard");
  }, [user, slug, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Bem-vinda(o) ao portal SUS!");
      nav("/dashboard");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  if (notFound) return (
    <div className="container-optia py-20 text-center">
      <h1 className="font-display text-2xl text-navy-900">Município não encontrado</h1>
      <p className="text-slate-600 mt-2">Verifique o link ou entre em contato com a secretaria de saúde.</p>
      <Link to="/" className="text-teal-700 mt-4 inline-block">Voltar ao início</Link>
    </div>
  );
  if (!muni) return <div className="container-optia py-20 text-slate-500 text-center">Carregando…</div>;

  return (
    <div className="container-optia py-12 animate-fade-in">
      <div className="grid lg:grid-cols-2 gap-10 items-start max-w-5xl mx-auto">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-teal-100 text-teal-800 px-3 py-1 text-xs" data-testid="sus-badge">
            <ShieldCheck className="w-3.5 h-3.5" /> Atendimento SUS
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold text-navy-900 mt-4" data-testid="sus-muni-name">
            {muni.name}
          </h1>
          {muni.state && <div className="text-slate-600">{muni.state}</div>}
          <p className="text-slate-600 mt-5 text-lg leading-relaxed">
            Portal exclusivo para pacientes do <b>SUS de {muni.name}</b> na Optia Saúde. Acesse com seu login para agendar consultas com nossa rede de profissionais — <b>sem custo para você</b>.
          </p>

          <div className="mt-6 space-y-3">
            <div className="flex items-start gap-3 text-sm text-slate-700">
              <div className="h-8 w-8 rounded-lg bg-teal-100 grid place-items-center text-teal-700"><HeartHandshake className="w-4 h-4" /></div>
              <div><b className="text-navy-900">Sem pagamento</b><div className="text-slate-600">Consultas cobertas pelo convênio SUS do município.</div></div>
            </div>
            <div className="flex items-start gap-3 text-sm text-slate-700">
              <div className="h-8 w-8 rounded-lg bg-teal-100 grid place-items-center text-teal-700"><ShieldCheck className="w-4 h-4" /></div>
              <div><b className="text-navy-900">Cadastro pela secretaria</b><div className="text-slate-600">Se você ainda não recebeu login, procure a UBS ou secretaria de saúde de {muni.name}.</div></div>
            </div>
          </div>
        </div>

        <Card className="card-soft">
          <CardContent className="p-7">
            <div className="flex items-center gap-3">
              <img src={LOGO} alt="" className="h-11 w-11 rounded-full" />
              <div>
                <div className="font-display font-semibold text-navy-900">Entrar no portal</div>
                <div className="text-xs text-slate-500">SUS · {muni.name}</div>
              </div>
            </div>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <div><Label>E-mail ou login SUS</Label><Input value={email} onChange={(e)=>setEmail(e.target.value)} required data-testid="sus-email" placeholder="ex: sus-12345678900@sus.optia.com.br" /></div>
              <div><Label>Senha</Label><Input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required data-testid="sus-password" /></div>
              <Button type="submit" disabled={busy} className="w-full bg-teal-500 hover:bg-teal-600 h-11 rounded-xl" data-testid="sus-login">{busy ? "Entrando..." : "Entrar"}</Button>
            </form>
            <p className="text-xs text-slate-500 mt-4 text-center">Cadastro só pela secretaria de saúde. Não fazemos cadastro online no portal SUS.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
